import { useId, type ReactElement } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { RunChartParams } from "@playbookai/widget-schemas";
import { KPIValue } from "../../atoms/KPIValue/index.js";
import { cn } from "../../lib/utils.js";
import { useReducedMotion } from "../../hooks/useReducedMotion.js";
import { ChartTooltip } from "../../molecules/ChartTooltip/index.js";
import type { WidgetBodyProps } from "../types.js";

/**
 * Run Chart — Specify, multi. Compact line chart with last-value headline
 * + optional target reference line. CSV/XLSX upload editor deferred — for
 * now the body is preview-only.
 *
 * Ported from ui-elements/components/solves/workspace/specify/RunChartCard.tsx.
 */
export function RunChartWidget({
  data,
}: WidgetBodyProps<RunChartParams>): ReactElement {
  const reducedMotion = useReducedMotion();
  const points = data.points;
  const unit = data.unit ?? "";
  const target = data.target;
  const lastPoint = points[points.length - 1];
  const targetState = runChartTargetState(lastPoint?.value, target);
  const gradientId = `run-chart-${useId().replace(/:/g, "")}`;
  const lineStroke =
    target === undefined ? "hsl(var(--pb-cyan))" : `url(#${gradientId})`;

  if (points.length === 0) {
    return (
      <div className="flex-1 grid place-items-center text-pb-text-muted text-xs italic px-4 text-center">
        Add a row, paste from a spreadsheet, or upload a CSV / XLSX file to see
        the trend.
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2">
      <div className="flex items-baseline justify-between shrink-0">
        <KPIValue
          size="lg"
          value={lastPoint?.value}
          unit={unit}
          deltaSlot={
            target != null ? (
              <span
                className={cn(
                  "ms-1 text-dense-1 uppercase tracking-wider font-semibold py-0.5 px-1.5 rounded-md border",
                  "border-pb-border bg-pb-card-elevated text-pb-text-muted",
                )}
              >
                {targetState?.label}
              </span>
            ) : null
          }
        />
        <span className="text-dense-1 text-pb-text-muted">
          {points[0]?.label} → {lastPoint?.label}
        </span>
      </div>
      <div className="flex-1 min-h-0">
        {/* Kept hand-built: recharts chart marks, which no design-system part draws. */}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={[...points]}
            margin={{ top: 8, right: 12, bottom: 4, left: -10 }}
          >
            {target !== undefined ? (
              <defs>
                <RunChartTargetGradient
                  id={gradientId}
                  points={points}
                  target={target}
                />
              </defs>
            ) : null}
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(var(--pb-border))"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={{ stroke: "hsl(var(--pb-border))" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "hsl(var(--pb-text-muted))" }}
              axisLine={false}
              tickLine={false}
              width={32}
            />
            <Tooltip
              cursor={{ stroke: "hsl(var(--pb-border))", strokeWidth: 1 }}
              content={
                <ChartTooltip
                  formatValue={(value) => `${String(value)} ${unit}`}
                />
              }
            />
            {target != null ? (
              <ReferenceLine
                y={target}
                stroke="hsl(var(--pb-amber))"
                strokeDasharray="4 4"
                label={{
                  value: `Target ${target}`,
                  position: "right",
                  fontSize: 10,
                  fill: "hsl(var(--pb-amber))",
                }}
              />
            ) : null}
            <Line
              type="monotone"
              dataKey="value"
              name="Value"
              stroke={lineStroke}
              strokeWidth={2.25}
              dot={(props) => {
                const { key, ...dotProps } = props;
                return (
                  <RunChartTargetDot
                    key={key}
                    {...dotProps}
                    {...(target === undefined ? {} : { target })}
                  />
                );
              }}
              activeDot={(props: {
                cx?: number;
                cy?: number;
                payload?: { value?: number };
              }) => (
                <RunChartTargetDot
                  {...props}
                  {...(target === undefined ? {} : { target })}
                  radius={5}
                />
              )}
              isAnimationActive={!reducedMotion}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export interface RunChartTargetState {
  readonly healthy: boolean;
  readonly label: "Below target" | "At target" | "Above target";
}

export function runChartTargetState(
  value: number | undefined,
  target: number | undefined,
): RunChartTargetState | null {
  if (value === undefined || target === undefined) return null;
  return {
    healthy: value >= target,
    label:
      value > target
        ? "Above target"
        : value === target
          ? "At target"
          : "Below target",
  };
}

export function RunChartTargetDot({
  cx = 0,
  cy = 0,
  payload,
  target,
  radius = 3,
}: {
  readonly cx?: number;
  readonly cy?: number;
  readonly payload?: { readonly value?: number };
  readonly target?: number;
  readonly radius?: number;
}): ReactElement {
  const state = runChartTargetState(payload?.value, target);
  const fill = state?.healthy
    ? "var(--color-pb-green)"
    : state
      ? "var(--color-pb-red)"
      : "hsl(var(--pb-cyan))";
  return <circle cx={cx} cy={cy} r={radius} fill={fill} strokeWidth={0} />;
}

export interface RunChartGradientStop {
  readonly offset: number;
  readonly color: string;
}

const HEALTHY_COLOR = "var(--color-pb-green)";
const CRITICAL_COLOR = "var(--color-pb-red)";

export function runChartTargetGradientStops(
  points: ReadonlyArray<{ readonly value: number }>,
  target: number,
): RunChartGradientStop[] {
  if (points.length === 0) return [];
  if (points.length === 1) {
    return [
      {
        offset: 0,
        color: points[0]!.value >= target ? HEALTHY_COLOR : CRITICAL_COLOR,
      },
      {
        offset: 100,
        color: points[0]!.value >= target ? HEALTHY_COLOR : CRITICAL_COLOR,
      },
    ];
  }

  const stops: RunChartGradientStop[] = [];
  const colorFor = (value: number): string =>
    value >= target ? HEALTHY_COLOR : CRITICAL_COLOR;
  stops.push({ offset: 0, color: colorFor(points[0]!.value) });
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1]!.value;
    const current = points[index]!.value;
    const startColor = colorFor(previous);
    const endColor = colorFor(current);
    const endOffset = (index / (points.length - 1)) * 100;
    if (startColor !== endColor && current !== previous) {
      const crossingFraction = (target - previous) / (current - previous);
      const crossingOffset =
        ((index - 1 + crossingFraction) / (points.length - 1)) * 100;
      stops.push({ offset: crossingOffset, color: startColor });
      stops.push({ offset: crossingOffset, color: endColor });
    }
    stops.push({ offset: endOffset, color: endColor });
  }
  return stops;
}

export function RunChartTargetGradient({
  id,
  points,
  target,
}: {
  readonly id: string;
  readonly points: ReadonlyArray<{ readonly value: number }>;
  readonly target: number;
}): ReactElement {
  return (
    <linearGradient
      id={id}
      data-testid="run-chart-target-gradient"
      x1="0%"
      x2="100%"
      y1="0%"
      y2="0%"
    >
      {runChartTargetGradientStops(points, target).map((stop, index) => (
        <stop
          key={`${stop.offset}-${stop.color}-${index}`}
          offset={`${stop.offset}%`}
          stopColor={stop.color}
        />
      ))}
    </linearGradient>
  );
}
